import type { Parcel } from "./types";

/**
 * Mirrors apps/customer/src/domain/parcelTypes.ts and parcelCategories.ts --
 * same reasoning as types.ts's own header comment: the mobile app and this
 * one are separate bundles, so this is copied rather than imported, but the
 * catalogue itself must not drift between the two.
 */
export interface ParcelType {
  id: string;
  label: string;
  hint: string;
  preset: Omit<Parcel, "description">;
}

export const PARCEL_TYPES: ParcelType[] = [
  {
    id: "documents",
    label: "Documents",
    hint: "Loose printed documents, A4 or smaller",
    preset: { length_mm: 350, width_mm: 250, height_mm: 10, weight_g: 250 },
  },
  {
    id: "flyer",
    label: "Flyer",
    hint: "Soft-sided, small and light, up to 2 kg",
    preset: { length_mm: 400, width_mm: 300, height_mm: 80, weight_g: 1000 },
  },
  {
    id: "small_parcel",
    label: "Small parcel",
    hint: "Shoebox sized, up to 5 kg",
    preset: { length_mm: 300, width_mm: 230, height_mm: 300, weight_g: 2000 },
  },
  {
    id: "medium_parcel",
    label: "Medium parcel",
    hint: "Moving-box sized, up to 10 kg",
    preset: { length_mm: 600, width_mm: 410, height_mm: 190, weight_g: 5000 },
  },
  {
    id: "large_parcel",
    label: "Large parcel",
    hint: "Bulky, up to 50 kg",
    preset: { length_mm: 1000, width_mm: 1000, height_mm: 1000, weight_g: 15000 },
  },
  {
    id: "international_package",
    label: "International package",
    hint: "Crossing a border -- sizes vary, adjust the dimensions below",
    preset: { length_mm: 450, width_mm: 350, height_mm: 300, weight_g: 5000 },
  },
  {
    id: "excess_baggage",
    label: "Excess baggage",
    hint: "A bag or case -- sizes vary, adjust the dimensions below",
    preset: { length_mm: 700, width_mm: 450, height_mm: 300, weight_g: 20000 },
  },
  {
    id: "other",
    label: "Other",
    hint: "Enter your own dimensions",
    preset: { length_mm: 300, width_mm: 200, height_mm: 150, weight_g: 1000 },
  },
];

export const EMPTY_PARCEL: Parcel = { description: "", length_mm: 300, width_mm: 200, height_mm: 150, weight_g: 1000 };

/**
 * "What's inside?" -- separate from ParcelType (which is about size/form
 * factor). Standard courier prohibited/restricted/dangerous-goods lists
 * reconciled into one scale; where sources disagreed on how strict an item
 * is, the stricter level was kept.
 *
 * Levels:
 *   none        no restriction -- ships as a normal parcel
 *   restricted  ships, but may need special handling or packaging
 *   dangerous   ships only with a carrier that accepts dangerous goods
 *   prohibited  cannot be shipped by courier at all
 */
export type RestrictionLevel = "none" | "restricted" | "dangerous" | "prohibited";

export interface ParcelCategory {
  id: string;
  label: string;
  level: RestrictionLevel;
}

export const PARCEL_CATEGORIES: ParcelCategory[] = [
  { id: "documents", label: "Documents / paperwork", level: "none" },
  { id: "clothing_household", label: "Clothing, household goods & general merchandise", level: "none" },
  { id: "electronics", label: "Electronics & appliances", level: "none" },
  { id: "food", label: "Food items (baked goods, non-perishable)", level: "restricted" },
  { id: "fragile", label: "Fragile items (glass, crockery, picture frames, shower screens)", level: "restricted" },
  { id: "furniture", label: "Furniture", level: "restricted" },
  { id: "beauty", label: "Perfume & cosmetics", level: "restricted" },
  { id: "vehicle_parts", label: "Motor vehicle parts containing liquid or oil", level: "restricted" },
  { id: "motorbike", label: "Motorbike / scooter", level: "restricted" },
  { id: "liquids", label: "Liquids (non-hazardous)", level: "restricted" },
  { id: "artwork", label: "Artwork, paintings & sculptures", level: "prohibited" },
  { id: "jewellery", label: "Jewellery, precious stones & watches", level: "prohibited" },
  { id: "cash_valuables", label: "Cash, coins, cheques, credit cards, bonds, securities & bullion", level: "prohibited" },
  { id: "antiques", label: "Antiques", level: "prohibited" },
  { id: "furs", label: "Furs", level: "prohibited" },
  { id: "animals", label: "Livestock & live animals", level: "prohibited" },
  { id: "medical", label: "Medical specimens / biological samples", level: "prohibited" },
  { id: "human_remains", label: "Human remains", level: "prohibited" },
  { id: "weapons", label: "Weapons & firearms", level: "prohibited" },
  { id: "ammunition", label: "Ammunition", level: "prohibited" },
  { id: "explosives", label: "Explosives & fireworks", level: "prohibited" },
  { id: "illegal", label: "Illegal substances or items", level: "prohibited" },
  { id: "flammable_liquids", label: "Flammable liquids, paints, thinners & fuels", level: "prohibited" },
  { id: "gas", label: "Compressed or flammable gas (butane, propane, aerosols, fire extinguishers)", level: "prohibited" },
  { id: "alcohol", label: "Alcohol", level: "prohibited" },
  { id: "toxic", label: "Toxic, poisonous or infectious substances", level: "prohibited" },
  { id: "combustibles", label: "Matches, charcoal & fire lighters", level: "prohibited" },
  { id: "acids", label: "Acids & corrosive chemicals", level: "dangerous" },
  { id: "magnetised", label: "Magnetised material", level: "dangerous" },
  { id: "agricultural", label: "Fertiliser & pesticides", level: "dangerous" },
  { id: "batteries", label: "Batteries", level: "dangerous" },
  { id: "hazardous_other", label: "Other dangerous or hazardous goods", level: "dangerous" },
  { id: "other", label: "Other", level: "none" },
];

export function restrictionMessage(level: RestrictionLevel): string | null {
  switch (level) {
    case "prohibited":
      return "This cannot be shipped by courier. Choose a different item, or contact us if you believe this is wrong.";
    case "dangerous":
      return "This is a dangerous good. Only some services can carry it, and it may need special declaration or packaging.";
    case "restricted":
      return "This item may need special handling or packaging. Pack it securely and let the driver know at collection.";
    default:
      return null;
  }
}
